import { motion } from "framer-motion";
import {
  Briefcase,
  GraduationCap,
  Calendar,
  Sparkles,
  Award,
  MapPin,
  User, // added for header icon
} from "lucide-react";
import aboutData from "../Data/aboutData";
import me from "../assets/images/me2.png";
import { useState } from "react";

const About = () => {
  const [imgError, setImgError] = useState(false);
  const { profile, timeline, education } = aboutData;

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  return (
    <section id="about" className="py-20 md:py-28 bg-white dark:bg-black">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section header (like CareerTimeline) */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-3xl  text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
            <User className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
            The Story So Far
          </h2>
          <div className="w-40 h-px bg-gray-500 dark:bg-gray-400 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 max-w-5xl mt-2 mx-auto text-base font-light">
            With 3+ years of creating innovative digital solutions, I bridge the gap between complex problems and elegant technical solutions.
          </p>
        </motion.div>
        

        {/* Two-column content with premium cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: Profile + Experiences */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Profile card */}
            <motion.div variants={fadeInUp} className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative bg-white dark:bg-gray-900 rounded-xl p-6 shadow-xl border border-gray-200 dark:border-gray-800 flex flex-col md:flex-row items-center gap-6">
                {/* Profile image */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  animate={{ y: [0, -5, 0] }}
                  transition={{
                    y: { repeat: Infinity, duration: 4, ease: "easeInOut" },
                    scale: { duration: 0.3 }
                  }}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-4 ring-white/50 dark:ring-gray-800/50 shadow-xl flex-shrink-0"
                >
                  {!imgError ? (
                    <img
                      src={me}
                      alt="Bouba Ahmed"
                      className="w-full h-full object-cover"
                      onError={() => setImgError(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-3xl font-light">
                      BA
                    </div>
                  )}
                </motion.div>
                {/* Name, title, location */}
                <div className="text-center md:text-left">
                  <h1 style={{ fontFamily: "Playwrite PL, cursive" }} className="text-3xl md:text-4xl font-light tracking-tight text-gray-900 dark:text-white mb-1">
                    Bouba Ahmed
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-gray-400 flex items-center justify-center md:justify-start gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    {profile?.title || "Fullstack Developer & AI Enthusiast"}
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <MapPin className="w-4 h-4" />
                    {profile?.location || "Khemisset, Morocco"}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Experiences card */}
            <motion.div variants={fadeInUp} className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-800">
                <h2 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-5 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Experiences
                </h2>
                <div className="space-y-5">
                  {timeline?.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="relative pl-5 border-l-2 border-indigo-200 dark:border-indigo-800 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors"
                    >
                      <div className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-indigo-500 dark:bg-indigo-400 border-2 border-white dark:border-gray-900" />
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {item.title}{" "}
                          <span className="text-indigo-600 dark:text-indigo-400 text-sm font-normal">
                            @{item.company}
                          </span>
                        </h3>
                        <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                          <Calendar className="w-3 h-3" />
                          {item.year}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Education */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Education card */}
            <motion.div variants={fadeInUp} className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition" />
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-800">
                <h2 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Education
                </h2>
                <div className="space-y-4">
                  {education?.map((edu, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                        <Award className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          {edu.degree}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {edu.institution} · {edu.period}
                        </p>
                        {edu.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {edu.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
